import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useUserItems } from 'api/aws/users/useUserItems'
import MultiUserSelect from 'components/Forms/MultiUserSelect'
import { handleFormatTypesString } from 'components/Forms/types'
import RenderDealWithAvatar from 'components/ui/render-deal-with-avatar'
import RenderProductWithIcon from 'components/ui/render-product-with-icon'
import format from 'date-fns/format'
import isEqual from 'lodash/isEqual'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { renderColorByString } from '../DataGrid/renderColorByString'
import './style.css'

function formatDate(timestamp) {
  if (!timestamp) return
  return format(new Date(timestamp), 'MM/dd/yyyy')
}

const TaskDrawerHeader = ({
  task,
  organization,
  deal,
  onUpdateMasterStatus,
  onChange,
  localAssignees,
  onSaveAssignees,
  isStaleData,
  localAssigner,
  onSaveAssigner,
}) => {
  const { PK, SK } = useParams()
  const theme = useTheme()
  const navigate = useNavigate()
  const { data: users, isLoading: isUsersLoading } = useUserItems()

  const currentAssignees = useMemo(() => {
    if (!task || !users || !task?.assignees?.length) return []
    const userIds = task?.assignees?.map(a => (a?.user_uuid ? a?.user_uuid : a))
    const uniqueIds = Array.from?.(new Set(userIds)) ?? []
    const assignees = users?.filter(u => uniqueIds?.includes(u?.user_uuid)) ?? []
    return assignees ?? []
  }, [task, users])

  const assignerUser = useMemo(() => {
    if (!task?.assigner || !users) return null
    const assignerId = typeof task.assigner === 'string' ? task.assigner : task.assigner?.user_uuid
    return users?.find(u => u?.user_uuid === assignerId) ?? null
  }, [task, users])

  const currentAssigner = useMemo(() => {
    const assignerId = localAssigner ?? (typeof task?.assigner === 'string' ? task.assigner : task?.assigner?.user_uuid)
    if (!assignerId || !users) return null
    return users?.find(u => u?.user_uuid === assignerId) ?? null
  }, [localAssigner, task, users])

  const taskType = task?.type ? handleFormatTypesString(task?.type ?? '—') : '—'

  const updateAssignees = () => {
    onSaveAssignees({ localAssignees, task })
  }

  const assignerChanged =
    localAssigner !== undefined &&
    localAssigner !== (typeof task?.assigner === 'string' ? task?.assigner : task?.assigner?.user_uuid)

  return (
    <Box
      sx={{
        p: 1,
        mb: 1,
        backgroundColor: theme.palette.background.defaultdim,
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.6)',
      }}
    >
      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          overflowY: 'hidden',
        }}
      >
        {/* Column 1: Master Status, Start Date, End Date */}
        <Grid item xs={12} sm={3} md={3} lg={3} xl={3} sx={{ py: 0.5, px: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="subtitle2" color="textSecondary">
              Master Status
            </Typography>
            <TextField
              select
              value={task?.masterStatus || ''}
              onChange={e => onUpdateMasterStatus(e.target.value, task)}
              variant="outlined"
              size="small"
              sx={{
                minWidth: 82,
                height: '32px',
                '& .MuiOutlinedInput-root': {
                  height: '32px',
                  padding: 0,
                  px: 1,
                  alignItems: 'center',
                },
                '& .MuiSelect-select': {
                  paddingRight: '2px !important',
                  paddingLeft: '0 !important',
                  padding: '4px 0px',
                  textAlign: 'right',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: renderColorByString(task?.masterStatus),
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.4)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(121, 212, 255, 0.8)',
                },
              }}
              SelectProps={{
                IconComponent: () => null,
                displayEmpty: true,
                MenuProps: {
                  PaperProps: {
                    sx: { fontSize: '0.875rem' },
                  },
                },
              }}
            >
              <MenuItem sx={{ color: renderColorByString('Not Started') }} value="Not Started">
                Not Started
              </MenuItem>
              <MenuItem sx={{ color: renderColorByString('In Progress') }} value="In Progress">
                In Progress
              </MenuItem>
              <MenuItem sx={{ color: renderColorByString('On Hold') }} value="On Hold">
                On Hold
              </MenuItem>
              <MenuItem sx={{ color: renderColorByString('Needs Review') }} disabled value="Needs Review">
                Needs Review
              </MenuItem>
              <MenuItem sx={{ color: renderColorByString('Need More Info') }} value="Need More Info">
                Need More Info
              </MenuItem>
              <MenuItem sx={{ color: renderColorByString('Revise') }} disabled value="Revise">
                Revise
              </MenuItem>
              <MenuItem sx={{ color: renderColorByString('Done') }} value="Done">
                Done
              </MenuItem>
            </TextField>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Start Date
            </Typography>
            <Typography sx={{ textAlign: 'left', color: theme.palette.text.primary }}>
              {formatDate(task?.startDate)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              End Date
            </Typography>
            <Typography sx={{ textAlign: 'left', color: theme.palette.text.primary }}>
              {task?.endDate ? formatDate(task?.endDate) : '–'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Task Type
            </Typography>
            <Typography sx={{ color: renderColorByString(taskType) }}>{taskType}</Typography>
          </Box>
        </Grid>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Column 2: Assignees + Assigner (editable) */}
        <Grid item xs={12} sm={3} md={3} lg={3} xl={3} sx={{ py: 1, px: 1 }}>
          {/* Assignees (multi-select) */}
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Grid item xs={!isEqual(localAssignees, task?.assignees || []) ? 10 : 12}>
              <MultiUserSelect
                name="assignees"
                value={currentAssignees || task?.assignees || []}
                options={users ?? []}
                displayLabel="Assignees"
                onChange={newVal => onChange('assignees', newVal)}
                inputRef={null}
                disabled={false}
                singleSelect={false}
              />
            </Grid>
            <Grid item xs={2} ml={1} sx={{ display: !isEqual(localAssignees, task?.assignees || []) ? '' : 'none' }}>
              <Tooltip
                title={
                  !isEqual(localAssignees, task?.assignees || []) ? (
                    <Typography>Save Assignees</Typography>
                  ) : (
                    <Typography>Up to date</Typography>
                  )
                }
              >
                <IconButton
                  onClick={updateAssignees}
                  className={
                    !isEqual(localAssignees, task?.assignees || []) ? 'highlight-assignee-save' : 'assignee-save-hide'
                  }
                >
                  <SaveIcon
                    className={
                      !isEqual(localAssignees, task?.assignees || []) ? 'highlight-assignee-save' : 'assignee-save-hide'
                    }
                    sx={{
                      fontSize: 28,
                      color: !isEqual(localAssignees, task?.assignees || []) ? 'text.highlight' : 'text.secondary',
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Grid>
          </Box>

          {/* Assigner (single-select, editable) */}
          <Box
            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}
          >
            <Grid item xs={assignerChanged ? 10 : 12}>
              <MultiUserSelect
                name="assigner"
                value={currentAssigner ? [currentAssigner] : []}
                options={users ?? []}
                displayLabel="Assigner"
                onChange={newVal => onChange('assigner', newVal?.[0]?.user_uuid ?? null)}
                inputRef={null}
                disabled={false}
                singleSelect={true}
              />
            </Grid>
            {assignerChanged && (
              <Grid item xs={2} ml={1}>
                <Tooltip title={<Typography>Save Assigner</Typography>}>
                  <IconButton onClick={() => onSaveAssigner?.(localAssigner)} className="highlight-assignee-save">
                    <SaveIcon className="highlight-assignee-save" sx={{ fontSize: 28, color: 'text.highlight' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Box>
        </Grid>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Column 3: Organization Status, Website, Task Type, Deal, Product */}
        <Grid item xs={12} sm={3} md={3} lg={3} xl={3} sx={{ py: 1, px: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Organization Status
            </Typography>
            <Typography sx={{ color: renderColorByString(organization?.status) }}>{organization?.status}</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Website
            </Typography>
            <Typography sx={{ height: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {organization?.website ?? '—'}
            </Typography>
          </Box>

          {/* Deal and Product */}
          <Stack>
            {deal && (
              <RenderDealWithAvatar
                deal={deal}
                sizes={{ height: 24, width: 24, py: 0, gap: 1, fontSize: 14 }}
                route={`/deals/${deal?.PK}/${deal?.SK}/details`}
                renderIcon={false}
              />
            )}
            {task && task?.relatedProduct && (
              <RenderProductWithIcon
                product={task?.relatedProduct}
                sizes={{
                  height: 24,
                  width: 24,
                  py: 0,
                  px: 0,
                  padding: 0,
                  gap: 1,
                  fontSize: 14,
                }}
                route={`/deals/${task?.relatedProduct?.PK}/DEAL_${task?.relatedProduct?.deal_uuid}/details`}
                renderIcon={false}
              />
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}

export default TaskDrawerHeader
